/**
 * API Authentication
 *
 * Handles access token management and refresh
 * @module portal/modules/api/auth
 */

import { state, saveAuth as saveAuthToStorage } from '../state.js';
import { createLogger } from '../../../shared/logger.js';
import { apiUrl, resolveGlobalConfig, type ApiConfig } from './config.js';

const log = createLogger('portal:api:auth');
const DEBUG = false;

/**
 * Ensure valid access token, refreshing if needed
 */
export async function ensureAccessToken(
  cfgOrForce?: ApiConfig | boolean,
  force = false
): Promise<string | null> {
  let cfg: ApiConfig | null = null;

  if (typeof cfgOrForce === 'boolean') {
    force = cfgOrForce;
    cfg = resolveGlobalConfig();
  } else {
    cfg = cfgOrForce ?? resolveGlobalConfig();
  }

  const now = Date.now();
  if (!force && state.extAccessToken && now < state.extExpiresAt) {
    if (DEBUG) {
      log.debug('using cached access token, expiresInMs=', state.extExpiresAt - now);
    }
    return state.extAccessToken;
  }

  if (state.extRefreshToken) {
    try {
      const response = await fetch(apiUrl(cfg, 'extensions/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: state.extRefreshToken }),
      });

      if (!response.ok) {
        throw new Error('refresh failed');
      }

      const data = await response.json();
      if (data && typeof data === 'object' && typeof data.accessToken === 'string') {
        state.extAccessToken = data.accessToken;
        state.extRefreshToken =
          typeof data.refreshToken === 'string' ? data.refreshToken : state.extRefreshToken;
        const exp = Number(data.expiresIn);
        state.extExpiresAt =
          Number.isFinite(exp) && exp > 0
            ? Date.now() + exp * 1000 - 30000
            : Date.now() + 2592000 * 1000;
        saveAuthToStorage({
          provider: 'auth0',
          tokenType: 'Bearer',
          accessToken: state.extAccessToken ?? '',
          refreshToken: state.extRefreshToken ?? undefined,
          expiresAt: state.extExpiresAt,
          user: state.user ?? undefined,
        });
        if (DEBUG) {
          log.debug('refreshed extension token, expiresInMs=', state.extExpiresAt - Date.now());
        }
        return state.extAccessToken || null;
      }
    } catch (error) {
      log.warn('Token refresh failed:', error);
      return null;
    }
  }

  return null;
}
